import { environment } from '../../../environments/environment';

type SupportedTokenizer = (
  details: TokenizerDetails
) => Promise<TokenizeResult>;

type TokenizeResult = {
  payload: CardConnectPayload;
  token: string;
  expiry?: string;
};

type TokenizerDetails = CardDetails | AchDetails;

type CardDetails = {
  type: 'card';
  number: string;
  expiry: string; // MM/YY
  cvc?: string;
  zip?: string;
  postal?: string;
  name?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  email?: string;
  phone?: string;
};

type AchDetails = {
  type: 'ach';
  name: string;
  routingNumber: string;
  accountNumber: string;
  accountType: 'Checking' | 'Savings';
  verification: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  postal?: string;
};

export type TokenizeDetails = CardDetails | AchDetails;
interface TokenizerWindow extends Window {
  TM_createPaymentToken?: SupportedTokenizer;
  CARD_CONNECT_MERCHANTID?: string;
  CardSecure?: {
    tokenize?: (
      details: Record<string, any>,
      onSuccess: (resp: Record<string, any>) => void,
      onError?: (err: Record<string, any>) => void
    ) => void;
  };
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function getTokenizerWindow(): TokenizerWindow | null {
  if (!isBrowser()) return null;
  return window as TokenizerWindow;
}

function resolveMerchantId(win: TokenizerWindow): string | undefined {
  const metaTag =
    typeof document !== 'undefined'
      ? document.querySelector<HTMLMetaElement>('meta[name="cardconnect:mid"]')
      : null;
  return (
    win.CARD_CONNECT_MERCHANTID ||
    metaTag?.content ||
    environment.payments?.cardConnectMerchantId ||
    undefined
  )?.trim();
}

function normalizeExpiry(expiry: string): string {
  const digits = expiry.replace(/\D/g, '');
  if (digits.length === 4) {
    return digits;
  }
  if (digits.length === 6) {
    return `${digits.slice(0, 2)}${digits.slice(4, 6)}`;
  }
  const [month = '', year = ''] = expiry.split('/');
  const mm = month.padStart(2, '0').slice(-2);
  const yy = year.trim().slice(-2);
  return `${mm}${yy}`;
}

export interface CardConnectPayload {
  type: string;
  merchid: string;
  account: string;
  expiry?: string;
  cvv2?: string;
  postal?: string;
  name?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  email?: string;
  phone?: string;
}

function createCardConnectTokenizer(win: TokenizerWindow): SupportedTokenizer {
  const baseUrl = environment.payments?.cardConnectUrl?.replace(/\/$/, '');
  const merchantId = resolveMerchantId(win);

  if (!baseUrl) {
    return () =>
      Promise.reject(
        new Error('CardConnect URL is not configured in the environment.')
      );
  }

  if (!merchantId) {
    return () =>
      Promise.reject(
        new Error(
          'CardConnect merchant ID is not available. Provide it via window.CARD_CONNECT_MERCHANTID or a <meta name="cardconnect:mid"> tag.'
        )
      );
  }

  const endpoint = `${baseUrl}/cardconnect/rest/cardsecure/tokenize`;

  return async (details: TokenizerDetails): Promise<TokenizeResult> => {
    const payload: CardConnectPayload = {
      type: details.type,
      merchid: merchantId,
      account: '',
      expiry: undefined,
    };

    if (details.type === 'card') {
      payload.account = details.number.replace(/\s+/g, '');
      if (!payload.account || payload.account.length === 0) {
        throw new Error('Card number is required.');
      }
      payload.expiry = normalizeExpiry(details.expiry);
      if (details.cvc) {
        payload.cvv2 = details.cvc.trim();
      }
    } else {
      // ACH Support:
      const routing = details.routingNumber.replace(/\D/g, '');
      const acct = details.accountNumber.replace(/\s+/g, '');
      payload.account = `${routing}/${acct}`;
      if (!routing || !acct || payload.account.length < 3) {
        throw new Error('ACH routing and account numbers are required.');
      }
    }

    payload.postal = details.postal?.trim();
    payload.name = details.name?.trim();
    payload.address = details.address?.trim();
    payload.city = details.city?.trim();
    payload.region = details.region?.trim();
    payload.country = details.country?.trim();
    payload.email = details.email?.trim();
    payload.phone = details.phone?.trim();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      mode: 'cors',
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `CardConnect tokenization failed (${response.status}): ${text}`
      );
    }

    const data = (await response.json()) as {
      token?: string;
      expiry?: string;
    };

    if (!data?.token) {
      throw new Error(
        'CardConnect tokenization response did not include a token.'
      );
    }

    return {
      payload,
      token: data.token,
      expiry: data.expiry,
    };
  };
}

function createUnsupportedTokenizer(): SupportedTokenizer {
  return () =>
    Promise.reject(
      new Error('Payment tokenization is not configured for this environment.')
    );
}

async function loadHostedCardSecure(win: TokenizerWindow): Promise<void> {
  if (win.CardSecure?.tokenize) return;
  const hostedUrl = environment.payments?.cardConnectHostedJsUrl;
  if (!hostedUrl) {
    throw new Error('CardConnect hosted JS URL is not configured.');
  }
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = hostedUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load CardConnect hosted JS'));
    document.head.appendChild(script);
  });
  if (!win.CardSecure?.tokenize) {
    throw new Error(
      'CardConnect hosted JS loaded but CardSecure.tokenize is unavailable.'
    );
  }
}

function createHostedCardSecureTokenizer(
  win: TokenizerWindow
): SupportedTokenizer {
  return async (details: TokenizerDetails): Promise<TokenizeResult> => {
    if (details.type !== 'card') {
      throw new Error(
        'Only card details are supported by the hosted tokenizer.'
      );
    }
    await loadHostedCardSecure(win);
    const mid = resolveMerchantId(win);
    if (!mid) {
      throw new Error(
        'CardConnect merchant ID is not available for hosted tokenizer.'
      );
    }
    // Common CardSecure shape; fields beyond these are ignored by the tokenizer
    const payload: CardConnectPayload = {
      account: details.number.replace(/\s+/g, ''),
      expiry: normalizeExpiry(details.expiry),
      cvv2: details.cvc,
      postal: details.zip || details.postal,
      name: details.name,
      address: details.address,
      city: details.city,
      region: details.region,
      country: details.country,
      email: details.email,
      phone: details.phone,
      merchid: mid,
      type: details.type,
    };

    return new Promise<TokenizeResult>((resolve, reject) => {
      win.CardSecure?.tokenize?.(
        payload,
        (resp) => {
          if (resp && resp['token']) {
            resolve({
              payload,
              token: resp['token'] as string,
              expiry:
                (resp['expiry'] as string | undefined) ||
                (payload['expiry'] as string | undefined),
            });
          } else {
            const msg =
              resp && resp['message']
                ? (resp['message'] as string)
                : 'CardConnect hosted tokenizer returned an error.';
            reject(new Error(msg));
          }
        },
        (err) => {
          const msg =
            err && err['message']
              ? (err['message'] as string)
              : 'CardConnect hosted tokenizer failed.';
          reject(new Error(msg));
        }
      );
    });
  };
}

export function installPaymentTokenizer(): void {
  const win = getTokenizerWindow();
  if (!win) return;

  if (typeof win.TM_createPaymentToken === 'function') {
    return;
  }

  let tokenizer: SupportedTokenizer;
  switch (environment.payments?.defaultType) {
    case 'fiserv':
      // Prefer hosted CardSecure if configured; fallback to REST tokenizer (may fail CORS in browser)
      if (environment.payments?.cardConnectHostedJsUrl) {
        tokenizer = createHostedCardSecureTokenizer(win);
      } else {
        tokenizer = createCardConnectTokenizer(win);
      }
      break;
    default:
      tokenizer = createUnsupportedTokenizer();
      break;
  }

  win.TM_createPaymentToken = tokenizer;
}

declare global {
  interface Window {
    TM_createPaymentToken?: SupportedTokenizer;
    CARD_CONNECT_MERCHANTID?: string;
  }
}

